import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FilePreviewHeader from './file-preview-header';
import FilePreviewDisplay from './file-preview-display';
import { closeFilePreview } from '@actions/attachment-actions';
import './file-preview.scss';

const FilePreview = () => {
  const dispatch = useDispatch();

  // Redux state
  const index = useSelector(state => state.attachments.index ?? -1);
  const attachments = useSelector(state => {
    const { attachments: allAttachments, contentType: ct, objectId: oid } = state.attachments;
    return allAttachments[ct + oid] || [];
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  // Sync currentIndex when index prop changes from negative to positive
  useEffect(() => {
    if (index >= 0) {
      setCurrentIndex(index);
    }
  }, [index]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = key => {
      if (attachments.length === 0) {
        return;
      }

      switch (key.code) {
      case 'ArrowLeft':
        setCurrentIndex(prev =>
          prev === 0 ? attachments.length - 1 : prev - 1
        );
        break;
      case 'ArrowRight':
        setCurrentIndex(prev =>
          prev === attachments.length - 1 ? 0 : prev + 1
        );
        break;
      default:
        // do nothing
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [attachments.length]);

  const closePreview = useCallback(() => {
    dispatch(closeFilePreview());
  }, [dispatch]);

  const getCurrentAsset = useCallback(() => {
    if (attachments && attachments.length > currentIndex) {
      return attachments[currentIndex];
    }
    return null;
  }, [attachments, currentIndex]);

  if (!attachments || attachments.length === 0 || index < 0) {
    return null;
  }

  const asset = getCurrentAsset();

  if (!asset) {
    return null;
  }

  return (
    <div styleName="file-preview-container">
      <FilePreviewHeader
        fileName={asset.file_name}
        total={attachments.length}
        current={currentIndex + 1}
        assetUrl={asset.signed_url}
        downloadUrl={asset.signed_download_url}
        closePreview={closePreview}
      />
      <FilePreviewDisplay asset={asset} />
    </div>
  );
};

export default FilePreview;
